'use client';

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { Smartphone, CalendarPlus, Users, ShieldAlert, Rocket } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";

const features = [
  {
    icon: Smartphone,
    title: "Automação via WhatsApp",
    description: "Automação inteligente do pós-operatório via WhatsApp.",
  },
  {
    icon: CalendarPlus,
    title: "Agendamento Integrado",
    description: "Agendamento de consultas integrado à plataforma.",
  },
  {
    icon: Users,
    title: "Plataforma Completa",
    description: "Perfis para médicos, secretárias e clínicas.",
  },
  {
    icon: ShieldAlert,
    title: "Alertas de Risco 24/7",
    description: "Notificações imediatas em casos de risco, 24/7.",
  },
  {
    icon: Rocket,
    title: "Implementação Rápida",
    description: "Implementação em 15 minutos, resultados em até 24h.",
  },
];

const solutionContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, when: "beforeChildren" },
  },
  exit: {
      opacity: 0,
      transition: { staggerChildren: 0.1, staggerDirection: -1, when: "afterChildren" }
  }
};

const solutionItemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
  exit: { opacity: 0, y: -30, transition: { duration: 0.3 } }
};

type SolutionProps = {
  isBot: boolean;
};

export default function Solution({ isBot }: SolutionProps) {
  const Wrapper = isBot ? "div" : motion.div;
  return (
    <section id="solution" className="relative pb-10 sm:pb-16 overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        <Wrapper
          variants={isBot ? undefined : solutionContainerVariants}
          initial={isBot ? undefined : "hidden"}
          whileInView={isBot ? undefined : "visible"}
          exit={isBot ? undefined : "exit"}
          viewport={isBot ? undefined : { once: false, amount: 0.2 }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-8">
            <div className="lg:w-1/3 flex justify-center mt-8 lg:mt-0 order-first lg:order-last">
                <Wrapper
                  variants={isBot ? undefined : solutionItemVariants}
                  className="relative w-[300px] h-[300px] lg:w-[400px] lg:h-[400px] lg:-mr-16"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: false, amount: 0.5 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full"
                >
                  <Image
                    src="/images/solution-image.png"
                    alt="Estetoscópio"
                    width={600}
                    height={600}
                    className="w-full h-full object-contain"
                  />
                </motion.div>
                </Wrapper>
            </div>
            <div className="lg:w-2/3 text-center lg:text-left">
                <Wrapper variants={isBot ? undefined : solutionItemVariants} className="lg:pl-16">
                   <h2 className="font-headline text-3xl sm:text-4xl tracking-[-0.04em] leading-[1.1] font-semibold text-black mb-4">
                    A Solução: Inteligência Médica Adaptativa <span className="text-primary">MedFollow</span>
                  </h2>
                </Wrapper>
                <Wrapper variants={isBot ? undefined : solutionItemVariants} className="lg:pl-16">
                  <p className="text-lg text-foreground/70 tracking-tight max-w-xl mx-auto lg:mx-0">
                    O MedFollow é um sistema que aprende seus protocolos para
                    automatizar a comunicação, filtrar o que é importante e devolver
                    o controle do seu tempo.
                  </p>
                </Wrapper>
            </div>
          </div>
        </Wrapper>

        <Wrapper
          initial={isBot ? undefined : "hidden"}
          whileInView={isBot ? undefined : "visible"}
          exit={isBot ? undefined : "exit"}
          viewport={isBot ? undefined : { once: false, amount: 0.1 }}
          variants={isBot ? undefined : solutionContainerVariants}
          className="mt-16 relative z-10"
        >
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-12 lg:grid-rows-2">
            {features.map((feature, index) => (
              <Wrapper
                  key={index}
                  variants={isBot ? undefined : solutionItemVariants}
                  className={cn(
                    "lg:col-span-4",
                    {
                      "lg:col-span-5": index === 1,
                      "lg:col-span-3": index === 2,
                      "lg.col-span-6": index === 3,
                    }
                  )}
              >
                <Card className={cn("h-full bg-card border-border hover:border-primary hover:shadow-lg transition-all transform hover:-translate-y-1", index % 2 === 0 ? "message-bubble" : "message-bubble-right")}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <feature.icon className="h-6 w-6 text-primary flex-shrink-0" />
                      <h3 className="text-lg font-semibold tracking-tight">{feature.title}</h3>
                    </div>
                    <p className="mt-2 text-sm text-muted-foreground tracking-tight">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </Wrapper>
            ))}
          </div>
        </Wrapper>
      </div>
    </section>
  );
}
